import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, TextField } from "@components/ui";
import { EnvelopeIcon, LockIcon, PersonIcon } from "@icons";
import { apiClient } from "@lib/apiClient";
import styles from "./Auth.module.css";

interface RegisterPageProps {
  onRegisterLogin: (token: string, user: { id: string; email: string; role: "SUPERADMIN" | "ADMIN" | "USER" }) => void;
}

interface RegisterResult {
  pending?: boolean;
  message?: string;
  token?: string;
  user?: { id: string; email: string; role: "SUPERADMIN" | "ADMIN" | "USER" };
}

export function RegisterPage({ onRegisterLogin }: RegisterPageProps) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await apiClient.post<RegisterResult>("/auth/register", {
        email,
        password,
        displayName: displayName || undefined,
      });
      // Superadmin gets a session immediately; everyone else is pending approval.
      if (data.token && data.user) {
        onRegisterLogin(data.token, data.user);
      } else {
        setPending(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Registration failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.root}>
      <aside className={styles.hero}>
        <div className={styles.aurora} aria-hidden>
          <span className={`${styles.auroraGlow} ${styles.auroraGlowA}`} />
          <span className={`${styles.auroraGlow} ${styles.auroraGlowB}`} />
        </div>
        <div className={styles.grain} aria-hidden />
        <div className={styles.heroInner}>
          <img src="/icons/icon.svg" alt="" className={styles.heroLogo} width={96} height={96} />
          <div className={styles.heroBrand}>
            <div className={styles.heroTitle}>Aria</div>
            <div className={styles.heroKicker}>by Aethera Healthcare</div>
          </div>
          <p className={styles.heroTag}>Secure voice, video, and messaging between you and your care team.</p>
        </div>
        <div className={styles.heroFoot}>A product of Aethera Healthcare Solutions</div>
      </aside>
      <div className={styles.panel}>
        <div className={styles.card}>
          {pending ? (
            <>
              <h1 className={styles.heading}>Request submitted</h1>
              <p className={styles.sub}>
                Your account was created and is awaiting administrator approval. You'll be able to sign in once it's
                approved.
              </p>
              <Link to="/login" className={styles.forgot}>Back to sign in</Link>
            </>
          ) : (
            <>
              <h1 className={styles.heading}>Request access</h1>
              <p className={styles.sub}>Create an account. An administrator approves new members before first sign-in.</p>
              <form onSubmit={submit} className={styles.form}>
                <TextField
                  id="displayName"
                  label="Name"
                  autoComplete="name"
                  leading={<PersonIcon />}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name"
                />
                <TextField
                  id="email"
                  type="email"
                  label="Email"
                  autoComplete="email"
                  leading={<EnvelopeIcon />}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
                <TextField
                  id="password"
                  type="password"
                  label="Password"
                  autoComplete="new-password"
                  leading={<LockIcon />}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                />
                {error && <p className={styles.error}>{error}</p>}
                <Button type="submit" fullWidth isLoading={loading}>Request access</Button>
                <p className={styles.switchAuth}>
                  Already have an account? <Link to="/login" className={styles.forgot}>Sign in</Link>
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
